import * as React from 'react';
import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { StatusTooltip } from './StatusTooltip';

const STATUS_CONFIG: Record<
  string,
  {
    label: string;
    Icon: React.ElementType;
    iconProps?: Record<string, unknown>;
    color: (theme: Theme) => string;
  }
> = {
  processing: {
    label: 'Processing',
    Icon: CircularProgress,
    iconProps: { size: 16 },
    color: (theme) => theme.palette.text.secondary,
  },
  warning: {
    label: 'Warning',
    Icon: WarningAmberIcon,
    color: (theme) => theme.palette.warning.main,
  },
  failed: {
    label: 'Error',
    Icon: ErrorIcon,
    color: (theme) => theme.palette.error.main,
  },
  completed: {
    label: 'Completed',
    Icon: CheckCircleIcon,
    color: (theme) => theme.palette.success.main,
  },
};

// Hoisted so the style objects aren't rebuilt for every row of the tree.
const WRAPPER_SX = { marginLeft: 'auto' } as const;

const ICON_SX_CACHE = new Map<string, Record<string, unknown>>();

const getIconSx = (status: string, color: (theme: Theme) => string) => {
  const cached = ICON_SX_CACHE.get(status);
  if (cached) return cached;
  const sx = { color, marginLeft: '6px' };
  ICON_SX_CACHE.set(status, sx);
  return sx;
};

export const FileStatus = React.memo(({ status }: { status: string }) => {
  const config = STATUS_CONFIG[status];
  if (!config) return null;

  const { Icon, iconProps = {} } = config;

  return (
    <Box sx={WRAPPER_SX}>
      <StatusTooltip title={config.label}>
        <Icon fontSize="small" {...iconProps} sx={getIconSx(status, config.color)} />
      </StatusTooltip>
    </Box>
  );
});
